'use client';
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Image from 'next/image';
import Link from 'next/link';

export default function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      window.location.href = '/';
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-[420px] xl:w-[480px] flex-col bg-card border-r border-border p-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
        <div className="flex items-center gap-3 mb-12 relative z-10">
          <AppLogo size={40} />
          <div>
            <p className="text-lg font-800 text-primary tracking-tight">URBANHUB</p>
            <p className="text-xs text-muted-foreground">Urban Trade · Internal Platform</p>
          </div>
        </div>
        <div className="flex-1 relative z-10">
          <h1 className="text-3xl font-800 text-foreground leading-tight mb-4">
            Secure access for every role
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8">
            URBANHUB enforces role-based access control across HR, payroll, contracts, and master data — ensuring each team member sees only what they need.
          </p>
          <div className="space-y-3">
            {[
              { icon: '🔐', text: 'Multi-role authentication with Supabase Auth' },
              { icon: '🛡️', text: 'Payroll & contracts locked to authorized roles' },
              { icon: '👁️', text: 'Audit trail for all sensitive data access' },
              { icon: '🏢', text: 'Covers 3 trade licenses, 157+ employees' },
            ].map((item, i) => (
              <div key={`feature-${i}`} className="flex items-start gap-3 text-sm text-muted-foreground">
                <span className="text-base flex-shrink-0 mt-0.5">{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-2xs text-muted-foreground relative z-10">
          © 2026 Urban Trade LLC · All rights reserved
        </p>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Full logo for sign-in panel */}
          <div className="flex justify-center mb-8">
            <Image
              src="/assets/images/UrbanLogo-Arabic-Final-removebg-preview-1780574243912.png"
              alt="URBANTRADE International Logo"
              width={260}
              height={100}
              className="object-contain"
              priority
            />
          </div>

          <div className="mb-8 text-center">
            <h2 className="text-2xl font-700 text-foreground">Sign in to your account</h2>
            <p className="text-sm text-muted-foreground mt-1">Enter your credentials to access URBANHUB</p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 bg-danger/10 border border-danger/20 text-danger rounded-lg px-4 py-3 mb-5 text-sm">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@urbanhub.ae"
                autoComplete="email"
                suppressHydrationWarning
                className="w-full px-3.5 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  suppressHydrationWarning
                  className="w-full px-3.5 py-2.5 pr-10 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  suppressHydrationWarning
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                href="/forgot-password"
                className="text-xs text-primary hover:text-primary/80 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              suppressHydrationWarning
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : (
                <LogIn size={16} />
              )}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-2xs text-muted-foreground mt-8">
            © 2026 Urban Trade International LLC · All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
}
